import { useEffect } from "react";
import { track } from "./analytics";

const IDLE_MS = 30000;
const HEARTBEAT_S = 15;

// Article view (≥1 s visible in an active tab), active reading heartbeats and scroll milestones.
export function useArticleEngagement(articleId, bodyRef) {
  useEffect(() => {
    if (!articleId) return undefined;
    let viewed = false;
    let stopped = false;
    let active = 0;
    let maxScroll = 0;
    let lastInput = Date.now();
    const sent = new Set();
    const visible = () => document.visibilityState === "visible" && document.hasFocus();
    const viewTimer = setTimeout(function check() {
      if (stopped) return;
      if (visible()) { viewed = true; track("article_view", {}, articleId); } else setTimeout(check, 1000);
    }, 1000);
    const onInput = () => { lastInput = Date.now(); };
    const onScroll = () => {
      onInput();
      const el = bodyRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const pct = Math.max(0, Math.min(100, Math.round(((window.innerHeight - r.top) / r.height) * 100)));
      maxScroll = Math.max(maxScroll, pct);
      [25, 50, 75, 100].forEach((d) => { if (pct >= d && !sent.has(d)) { sent.add(d); track("scroll_depth", { depth: d }, articleId); } });
    };
    const beat = () => {
      if (active > 0) track("engaged_read", { active_seconds: Math.min(60, active), max_scroll: maxScroll }, articleId);
      active = 0;
    };
    const tick = setInterval(() => {
      if (viewed && visible() && Date.now() - lastInput < IDLE_MS) {
        active += 1;
        if (active >= HEARTBEAT_S) beat();
      }
    }, 1000);
    const onHide = () => { if (document.visibilityState === "hidden") beat(); };
    ["scroll", "keydown", "pointerdown", "pointermove", "touchstart"].forEach((ev) => window.addEventListener(ev, ev === "scroll" ? onScroll : onInput, { passive: true }));
    document.addEventListener("visibilitychange", onHide);
    return () => {
      stopped = true;
      beat();
      clearTimeout(viewTimer);
      clearInterval(tick);
      ["scroll", "keydown", "pointerdown", "pointermove", "touchstart"].forEach((ev) => window.removeEventListener(ev, ev === "scroll" ? onScroll : onInput));
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [articleId, bodyRef]);
}
